import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useCreateLieu, useDeleteLieu, useLieux, useUpdateLieu } from '../../api/hooks';
import type { Lieu } from '../../api/types';
import { TIMEZONES } from '../../lib/labels';
import { Button, EmptyState, Field, Input, ROW, Section, Select, Spinner, TABLE, TD, TH, THEAD_ROW } from '../ui';
import { useToast } from '../toast';

const EMPTY: Omit<Lieu, 'idLieu'> = {
  libelle: '',
  ville: '',
  adresse: '',
  latitude: 48.8566,
  longitude: 2.3522,
  timezone: 'Europe/Paris',
};

export default function LieuxTab() {
  const toast = useToast();
  const { data, isPending } = useLieux();
  const create = useCreateLieu();
  const update = useUpdateLieu();
  const remove = useDeleteLieu();
  const [form, setForm] = useState(EMPTY);

  return (
    <div className="space-y-4">
      <Section title="Nouveau lieu">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Libellé">
            <Input aria-label="Libellé du lieu" value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} />
          </Field>
          <Field label="Ville">
            <Input aria-label="Ville" value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} />
          </Field>
          <Field label="Adresse">
            <Input aria-label="Adresse" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
          </Field>
          <Field label="Fuseau horaire" hint="Pilote l’offset des dates rendues à l’app">
            <Select aria-label="Fuseau horaire" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            variant="primary"
            size="sm"
            className="mb-1"
            disabled={create.isPending || !form.libelle}
            onClick={() =>
              create.mutate(form, {
                onSuccess: () => {
                  toast.success('Lieu créé');
                  setForm(EMPTY);
                },
                onError: (err) => toast.error(err),
              })
            }
          >
            Créer
          </Button>
        </div>
      </Section>

      <Section title="Lieux">
        {isPending ? <Spinner /> : null}
        {!isPending && !data?.length ? <EmptyState>Aucun lieu.</EmptyState> : null}
        {data?.length ? (
          <table className={TABLE}>
            <thead>
              <tr className={THEAD_ROW}>
                <th className={TH}>idLieu</th>
                <th className={TH}>Libellé</th>
                <th className={TH}>Ville</th>
                <th className={TH}>Fuseau</th>
                <th className={TH} />
              </tr>
            </thead>
            <tbody>
              {data.map((lieu) => (
                <tr key={lieu.idLieu} className={ROW}>
                  <td className={`${TD} font-mono text-xs text-outline`}>{lieu.idLieu}</td>
                  <td className={TD}>{lieu.libelle}</td>
                  <td className={TD}>{lieu.ville}</td>
                  <td className={TD}>
                    <Select
                      aria-label={`Fuseau de ${lieu.libelle}`}
                      value={lieu.timezone}
                      onChange={(e) =>
                        update.mutate(
                          { ...lieu, timezone: e.target.value },
                          { onSuccess: () => toast.success('Fuseau mis à jour'), onError: (err) => toast.error(err) },
                        )
                      }
                    >
                      {TIMEZONES.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className={TD}>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Supprimer ${lieu.libelle}`}
                      onClick={() =>
                        remove.mutate(lieu.idLieu, {
                          onSuccess: () => toast.success('Lieu supprimé'),
                          onError: (err) => toast.error(err),
                        })
                      }
                    >
                      <Trash2 size={15} aria-hidden />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Section>
    </div>
  );
}
